import type { DrawingDocument, NormalizedPoint } from '../model';
import { changeStructure, nextSequentialId } from './drawingDocumentCore.ts';
import { documentToAtoms, documentToLines } from './drawingDocumentProjections.ts';
import { canvasRings } from './objectEditing.ts';
import { isChemicalBond } from './documentSelectors.ts';
import { centroidOf } from './haptoCentroid.ts';

export { centroidOf };

// ADR 0026 D1-D3 (#1156) -- a hapto (η) bond: a metal bonded to two or more adjacent atoms of one
// ligand at once (η² ethylene, η⁵ cyclopentadienyl). In the editor it is a graph edge of type
// `hapto`, order 0, from the metal (`source`) to the first ligand atom (`target`), carrying all
// of them as `ligandAtomIds`; it joins the metal and the ligand into one molecule, adds no valence,
// and the chemistry engines never see it. The molecule document keeps it as `haptoBonds`.

/** Each hapto bond's ligand atoms, by bond id. */
export function haptoLigands(document: DrawingDocument): Readonly<Record<string, readonly string[]>> {
  return Object.fromEntries(
    Object.entries(document.structure.bonds).flatMap(([id, bond]) =>
      bond.type === 'hapto' && bond.ligandAtomIds ? [[id, bond.ligandAtomIds]] : []
    )
  );
}

/** Whether `atomIds` are joined to one another by the drawing's chemical bonds alone. */
function adjacent(document: DrawingDocument, atomIds: readonly string[]): boolean {
  const inSet = new Set(atomIds);
  const reached = new Set([atomIds[0]!]);
  for (let grown = true; grown;) {
    grown = false;
    for (const edge of document.structure.graph.edges) {
      if (!isChemicalBond(document.structure.bonds[edge.id])) continue;
      if (!inSet.has(edge.source) || !inSet.has(edge.target)) continue;
      if (reached.has(edge.source) !== reached.has(edge.target)) {
        reached.add(edge.source);
        reached.add(edge.target);
        grown = true;
      }
    }
  }
  return reached.size === inSet.size;
}

/**
 * ADR 0026 D2: bond `metalAtomId` to `ligandAtomIds` with a hapto bond, as one change. The same
 * document when the ligand is fewer than two atoms, includes the metal, isn't one adjacent run of
 * atoms, or the metal already has a hapto bond to those atoms.
 */
export function addHaptoBond(
  document: DrawingDocument,
  metalAtomId: string,
  ligandAtomIds: readonly string[]
): DrawingDocument {
  const ligand = [...new Set(ligandAtomIds)];
  const { structure } = document;
  if (
    ligand.length < 2 ||
    !structure.atoms[metalAtomId] ||
    ligand.includes(metalAtomId) ||
    ligand.some((id) => !structure.atoms[id]) ||
    !adjacent(document, ligand)
  )
    return document;
  const key = [...ligand].sort().join('|');
  const exists = structure.graph.edges.some((edge) => {
    const bond = structure.bonds[edge.id];
    return (
      bond?.type === 'hapto' && edge.source === metalAtomId && [...(bond.ligandAtomIds ?? [])].sort().join('|') === key
    );
  });
  if (exists) return document;
  const id = nextSequentialId(
    structure.graph.edges.map((edge) => edge.id),
    'e'
  );
  return changeStructure(document, {
    graph: {
      ...structure.graph,
      revision: structure.graph.revision + 1,
      edges: [...structure.graph.edges, { id, source: metalAtomId, target: ligand[0]! }]
    },
    bonds: {
      ...structure.bonds,
      [id]: { order: 0, type: 'hapto', ligandAtomIds: ligand as [string, string, ...string[]] }
    }
  });
}

const distanceBetween = (a: NormalizedPoint, b: NormalizedPoint | undefined) =>
  b ? Math.hypot(a.x - b.x, a.y - b.y) : Infinity;

const distanceToSegment = (point: NormalizedPoint, a: NormalizedPoint, b: NormalizedPoint) => {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const length = dx * dx + dy * dy;
  const t = length === 0 ? 0 : Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / length));
  return Math.hypot(point.x - (a.x + t * dx), point.y - (a.y + t * dy));
};

function insidePolygon(point: NormalizedPoint, polygon: readonly NormalizedPoint[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [a, b] = [polygon[i]!, polygon[j]!];
    if (a.y > point.y !== b.y > point.y && point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x) {
      inside = !inside;
    }
  }
  return inside;
}

/**
 * ADR 0026 D2: the ligand atoms a hapto bond drawn to `point` bonds to -- the selected atoms when
 * `point` is on one of them (η³ allyl, η⁴ diene), else the smallest ring `point` is inside or on an
 * atom of (η⁵, η⁶), else the bond within `reach` of it (η²). `point` and `reach` are in document
 * units. Empty when none.
 */
export function haptoLigandAt(
  document: DrawingDocument,
  point: NormalizedPoint,
  reach: number,
  selectedAtomIds: readonly string[] = []
): readonly string[] {
  const { coordinates } = document.depiction.coordinateSet;
  const atoms = documentToAtoms(document);
  const lines = documentToLines(document).filter((line) => isChemicalBond(document.structure.bonds[line.id]));
  const atomAt = atoms
    .map((atom) => ({ atom, distance: distanceBetween(point, coordinates[atom.id]) }))
    .filter(({ distance }) => distance <= reach)
    .sort((a, b) => a.distance - b.distance)[0]?.atom;
  if (atomAt && selectedAtomIds.length >= 2 && selectedAtomIds.includes(atomAt.id)) return selectedAtomIds;
  const rings = canvasRings(atoms, lines);
  const ring =
    rings.find((candidate) => atomAt !== undefined && candidate.atomIds.includes(atomAt.id)) ??
    rings.find((candidate) =>
      insidePolygon(
        point,
        candidate.atomIds.flatMap((id) => (coordinates[id] ? [coordinates[id]] : []))
      )
    );
  if (ring) return ring.atomIds;
  const nearest = document.structure.graph.edges
    .filter((edge) => isChemicalBond(document.structure.bonds[edge.id]))
    .flatMap((edge) => {
      const [a, b] = [coordinates[edge.source], coordinates[edge.target]];
      return a && b ? [{ edge, distance: distanceToSegment(point, a, b) }] : [];
    })
    .filter(({ distance }) => distance <= reach)
    .sort((a, b) => a.distance - b.distance)[0];
  return nearest ? [nearest.edge.source, nearest.edge.target] : [];
}

/**
 * ADR 0026 (#1156) -- the drawing as the chemistry engines get it: the hapto bonds left out (they
 * already are, being no chemical bond), and an odd ligand drawn with a saturated carbon -- η³
 * allyl drawn as propene, η⁵ cyclopentadienyl drawn as cyclopentadiene -- handed over as its
 * radical, the neutral ligand the metal binds (one electron on that carbon, so C₅H₆ reads C₅H₅).
 * An even ligand (η² ethylene, η⁶ benzene) or one drawn charged (Cp⁻) is left as drawn. The same
 * document without hapto bonds. The server's MoleculeCdkConverter applies the same rule.
 */
export function haptoLigandsForEngines(document: DrawingDocument): DrawingDocument {
  const ligands = Object.values(haptoLigands(document));
  if (ligands.length === 0) return document;
  const { structure } = document;
  const multiplyBonded = new Set<string>();
  for (const edge of structure.graph.edges) {
    const bond = structure.bonds[edge.id];
    if (!isChemicalBond(bond) || (bond?.order ?? 1) < 2) continue;
    multiplyBonded.add(edge.source);
    multiplyBonded.add(edge.target);
  }
  const radical = new Set<string>();
  for (const atomIds of ligands) {
    if (atomIds.length % 2 === 0) continue;
    const saturated = atomIds.find(
      (id) =>
        structure.atoms[id]?.atomicNumber === 6 &&
        !multiplyBonded.has(id) &&
        !structure.atoms[id]?.radicalElectronCount &&
        (structure.formalCharges[id] ?? 0) === 0 &&
        !radical.has(id)
    );
    if (saturated) radical.add(saturated);
  }
  if (radical.size === 0) return document;
  return {
    ...document,
    structure: {
      ...structure,
      atoms: Object.fromEntries(
        Object.entries(structure.atoms).map(([id, atom]) => [
          id,
          radical.has(id) ? { ...atom, radicalElectronCount: 1 } : atom
        ])
      )
    }
  };
}

/**
 * ADR 0026 -- a fresh layout of a saved molecule (a search card, a reaction participant) comes from
 * RDKit, which never sees the hapto bonds and so places the metal and its ligands anywhere. Each
 * ligand (with what is bonded to it) is moved next to its metal, its middle `distance` away: the
 * first to the left, the second to the right, then above and below -- a sandwich reads as one.
 * `position` holds each atom's layout point by molecule atom id and is changed in place; `edges`
 * are the molecule's own (chemical) bonds.
 */
export function placeHaptoLigands(
  haptoBonds: Readonly<Record<string, { readonly metalAtomId: string; readonly ligandAtomIds: readonly string[] }>>,
  edges: readonly { readonly source: string; readonly target: string }[],
  position: Map<string, NormalizedPoint>,
  distance: number
): void {
  const neighbours = new Map<string, string[]>();
  for (const { source, target } of edges) {
    neighbours.set(source, [...(neighbours.get(source) ?? []), target]);
    neighbours.set(target, [...(neighbours.get(target) ?? []), source]);
  }
  const directions = [
    { x: -1, y: 0 },
    { x: 1, y: 0 },
    { x: 0, y: 1 },
    { x: 0, y: -1 }
  ];
  const placedPerMetal = new Map<string, number>();
  for (const { metalAtomId, ligandAtomIds } of Object.values(haptoBonds)) {
    const metal = position.get(metalAtomId);
    const middle = centroidOf(ligandAtomIds, (id) => position.get(id));
    if (!metal || !middle) continue;
    // The ligand's whole piece, without crossing the metal.
    const piece = new Set(ligandAtomIds);
    const queue = [...ligandAtomIds];
    while (queue.length) {
      for (const next of neighbours.get(queue.shift()!) ?? []) {
        if (next === metalAtomId || piece.has(next)) continue;
        piece.add(next);
        queue.push(next);
      }
    }
    if ([...piece].some((id) => (neighbours.get(id) ?? []).includes(metalAtomId))) continue;
    const index = placedPerMetal.get(metalAtomId) ?? 0;
    placedPerMetal.set(metalAtomId, index + 1);
    const direction = directions[index % directions.length]!;
    const reach = distance * (1 + Math.floor(index / directions.length));
    const dx = metal.x + direction.x * reach - middle.x;
    const dy = metal.y + direction.y * reach - middle.y;
    for (const id of piece) {
      const point = position.get(id);
      if (point) position.set(id, { x: point.x + dx, y: point.y + dy });
    }
  }
}
